// How a candidate's shared Sebapoznanie results relate to a position, described for both sides:
// the company reads "where it fits" and "what to ask at the interview", the candidate reads
// "what suits you" and "what to prepare". Both come from the same rules, so the candidate sees
// before applying exactly what the company would.
//
// Works only from the shared snapshots (`traits`), never from raw scores, and never adds up to a
// percentage or a ranking: the tests are self-reflection, not a selection tool, and a hiring
// decision must not rest on an automated score (GDPR art. 22).

import type { JobWorkProfile } from '../types/Job.ts'
import type { SharedAssessment } from '../types/JobRequest.ts'
import { SurveyTypes } from '../types/Survey.ts'

/** The tests a candidate can share with a job application, in the order they are offered. */
export const JOB_APPLICATION_TESTS: SurveyTypes[] = [
    SurveyTypes.HOLLAND,
    SurveyTypes.BIG_FIVE,
    SurveyTypes.VIA,
    SurveyTypes.SDT,
    SurveyTypes.EXCELLENCE,
    SurveyTypes.LEADERSHIP_STYLES,
    SurveyTypes.ENTREPRENEURSHIP
]

/** Holland areas, short enough to list after a colon */
const AREAS: Record<string, string> = {
    R: 'technika a praktická práca',
    I: 'analýza a riešenie problémov',
    A: 'tvorba a nápady',
    S: 'práca s ľuďmi a pomáhanie',
    E: 'vedenie, obchod a presviedčanie',
    C: 'dáta, pravidlá a štruktúra'
}

export const WORK_FOCUS_OPTIONS = (Object.keys(AREAS) as string[]).map((value) => ({ value, label: AREAS[value]! }))
export const WORK_FOCUS_MAX = 2

export const COLLABORATION_OPTIONS: Array<{ value: NonNullable<JobWorkProfile['collaboration']>; label: string }> = [
    { value: 'solo', label: 'Prevažne samostatne' },
    { value: 'mixed', label: 'Striedavo samostatne aj v tíme' },
    { value: 'team', label: 'Prevažne v tíme a s ľuďmi' }
]

export const CHANGE_OPTIONS: Array<{ value: NonNullable<JobWorkProfile['change']>; label: string }> = [
    { value: 'stable', label: 'Ustálené, s jasnými postupmi' },
    { value: 'mixed', label: 'Niečo medzi' },
    { value: 'changing', label: 'Veci sa často menia' }
]

export type FitAudience = 'company' | 'candidate'

export type FitPoint = {
    /** `match`: the position and the result fit; `ask`: worth talking through at the interview */
    kind: 'match' | 'ask'
    text: string
}

export function hasWorkProfile(profile?: JobWorkProfile | null): profile is JobWorkProfile {
    return !!(
        profile?.focus?.length ||
        (profile?.collaboration && profile.collaboration !== 'mixed') ||
        (profile?.change && profile.change !== 'mixed') ||
        profile?.leadsPeople
    )
}

// Semicolons: an area name can hold a comma itself
const list = (letters: string[]) => letters.map((letter) => AREAS[letter]).filter(Boolean).join('; ')

/**
 * Where the shared results meet the position. Empty when the position has no work profile or
 * nothing relevant was shared; a point is only made from a test that was shared.
 */
export function getJobFit(
    profile: JobWorkProfile | null | undefined,
    assessments: SharedAssessment[] | null | undefined,
    audience: FitAudience
): FitPoint[] {
    if (!hasWorkProfile(profile) || !assessments?.length) return []

    const company = audience === 'company'
    const shared = (type: SurveyTypes) => assessments.find((assessment) => assessment.type === type)
    const traits = (type: SurveyTypes) => new Set(shared(type)?.traits ?? [])
    const points: FitPoint[] = []

    const holland = shared(SurveyTypes.HOLLAND)?.traits ?? []
    const bigFive = traits(SurveyTypes.BIG_FIVE)
    const excellence = traits(SurveyTypes.EXCELLENCE)
    const entrepreneurship = traits(SurveyTypes.ENTREPRENEURSHIP)
    const sharedBigFive = !!shared(SurveyTypes.BIG_FIVE)

    // What the work is about vs. what the candidate enjoys (Holland)
    if (profile.focus?.length && holland.length) {
        const top = holland.slice(0, 2)
        const inTop = profile.focus.filter((letter) => top.includes(letter))
        const inThree = profile.focus.filter((letter) => holland.includes(letter))

        if (inTop.length) {
            points.push({
                kind: 'match',
                text: company
                    ? `Pozícia je o tom, čo uchádzača najviac baví: ${list(inTop)}.`
                    : `Táto práca je o tom, čo ťa najviac baví: ${list(inTop)}.`
            })
        } else if (inThree.length) {
            points.push({
                kind: 'match',
                text: company
                    ? `Pozícia je o oblasti, ktorá patrí medzi tri najsilnejšie záujmy uchádzača: ${list(inThree)}.`
                    : `Táto práca je o oblasti, ktorá patrí medzi tvoje tri najsilnejšie záujmy: ${list(inThree)}.`
            })
        } else {
            points.push({
                kind: 'ask',
                text: company
                    ? `Pozícia je najmä o oblasti: ${list(profile.focus)}. Najsilnejšie záujmy uchádzača sú inde: ${list(top)}. Spýtajte sa, čo uchádzača na pozícii láka.`
                    : `Táto práca je najmä o oblasti: ${list(profile.focus)}. Tvoje najsilnejšie záujmy sú inde: ${list(top)}. Priprav si odpoveď, čo ťa na nej láka.`
            })
        }
    }

    // Alone or with people (Big Five)
    if (profile.collaboration === 'team' && sharedBigFive) {
        points.push(
            bigFive.has('extraversion') || bigFive.has('agreeableness')
                ? {
                      kind: 'match',
                      text: company
                          ? 'Pozícia je tímová a uchádzačovi práca s ľuďmi sedí.'
                          : 'Táto práca je tímová a práca s ľuďmi ti sedí.'
                  }
                : {
                      kind: 'ask',
                      text: company
                          ? 'Pozícia je tímová. Spýtajte sa, v akom tíme sa uchádzačovi pracovalo najlepšie.'
                          : 'Táto práca je tímová. Premysli si, v akom tíme sa ti pracovalo najlepšie, firma sa na to môže spýtať.'
                  }
        )
    }
    if (profile.collaboration === 'solo' && sharedBigFive) {
        if (bigFive.has('extraversion')) {
            points.push({
                kind: 'ask',
                text: company
                    ? 'Pozícia je prevažne samostatná a uchádzač čerpá energiu z kontaktu s ľuďmi. Spýtajte sa, ako zvláda dlhšiu samostatnú prácu.'
                    : 'Táto práca je prevažne samostatná a ty čerpáš energiu z ľudí. Premysli si, ako ti ide dlhšia samostatná práca.'
            })
        } else if (bigFive.has('conscientiousness')) {
            points.push({
                kind: 'match',
                text: company
                    ? 'Pozícia je prevažne samostatná a uchádzač pracuje systematicky a spoľahlivo.'
                    : 'Táto práca je prevažne samostatná a ty pracuješ systematicky a spoľahlivo.'
            })
        }
    }

    // Stable or changing environment (Big Five, excellence, entrepreneurship)
    const likesChange =
        bigFive.has('openness') ||
        excellence.has('adaptability') ||
        entrepreneurship.has('risk_taking') ||
        entrepreneurship.has('innovation')
    const sharedChangeTests =
        sharedBigFive || !!shared(SurveyTypes.EXCELLENCE) || !!shared(SurveyTypes.ENTREPRENEURSHIP)

    if (profile.change === 'changing' && sharedChangeTests) {
        points.push(
            likesChange
                ? {
                      kind: 'match',
                      text: company
                          ? 'Prostredie sa často mení a uchádzačovi zmeny sedia.'
                          : 'Prostredie sa tu často mení a zmeny ti sedia.'
                  }
                : {
                      kind: 'ask',
                      text: company
                          ? 'Prostredie sa často mení. Spýtajte sa na situáciu, keď sa uchádzačovi plány zmenili na poslednú chvíľu.'
                          : 'Prostredie sa tu často mení. Priprav si príklad, keď sa ti plány zmenili na poslednú chvíľu.'
                  }
        )
    }
    if (profile.change === 'stable') {
        const likesStructure =
            bigFive.has('conscientiousness') || excellence.has('quality_standards') || holland.slice(0, 2).includes('C')
        const likesNovelty = entrepreneurship.has('risk_taking') || entrepreneurship.has('innovation')

        if (likesStructure) {
            points.push({
                kind: 'match',
                text: company
                    ? 'Pozícia má jasné a ustálené postupy a uchádzačovi štruktúra sedí.'
                    : 'Táto práca má jasné postupy a štruktúra ti sedí.'
            })
        } else if (likesNovelty) {
            points.push({
                kind: 'ask',
                text: company
                    ? 'Pozícia má ustálené postupy a uchádzača baví skúšať nové veci. Spýtajte sa, kde vidí priestor na zlepšenie.'
                    : 'Táto práca má ustálené postupy a teba baví skúšať nové veci. Premysli si, kde vidíš priestor na zlepšenie.'
            })
        }
    }

    // Leading people (leadership styles, Holland, VIA)
    if (profile.leadsPeople) {
        const leadership = shared(SurveyTypes.LEADERSHIP_STYLES)
        const likesLeading = holland.slice(0, 2).includes('E') || traits(SurveyTypes.VIA).has('leadership')

        if (leadership?.primaryLabel) {
            points.push({
                kind: 'match',
                text: company
                    ? `Pozícia zahŕňa vedenie ľudí. Štýl vedenia uchádzača: ${leadership.primaryLabel}. Spýtajte sa na konkrétnu situáciu z vedenia tímu.`
                    : `Táto práca zahŕňa vedenie ľudí. Tvoj štýl vedenia: ${leadership.primaryLabel}. Priprav si príklad z vedenia tímu.`
            })
        } else if (likesLeading) {
            points.push({
                kind: 'match',
                text: company
                    ? 'Pozícia zahŕňa vedenie ľudí a uchádzača baví viesť a rozbiehať veci.'
                    : 'Táto práca zahŕňa vedenie ľudí a teba baví viesť a rozbiehať veci.'
            })
        } else {
            points.push({
                kind: 'ask',
                text: company
                    ? 'Pozícia zahŕňa vedenie ľudí. Spýtajte sa na skúsenosti s vedením.'
                    : 'Táto práca zahŕňa vedenie ľudí. Priprav si príklad, keď si viedol/viedla tím alebo projekt.'
            })
        }
    }

    return points
}

/**
 * The next test worth taking for this position: the ones its work profile is matched against
 * first, then the rest of JOB_APPLICATION_TESTS. Null when all are done.
 */
export function recommendTestForJob(
    profile: JobWorkProfile | null | undefined,
    doneTypes: Iterable<SurveyTypes>
): SurveyTypes | null {
    const done = new Set(doneTypes)
    const relevant: SurveyTypes[] = []

    if (profile?.focus?.length) relevant.push(SurveyTypes.HOLLAND)
    if (profile?.collaboration && profile.collaboration !== 'mixed') relevant.push(SurveyTypes.BIG_FIVE)
    if (profile?.change && profile.change !== 'mixed') relevant.push(SurveyTypes.BIG_FIVE, SurveyTypes.EXCELLENCE)
    if (profile?.leadsPeople) relevant.push(SurveyTypes.LEADERSHIP_STYLES)

    return [...relevant, ...JOB_APPLICATION_TESTS].find((type) => !done.has(type)) ?? null
}
